/** Application shell: navigation, shared config/template loading, tier state. */

import { useCallback, useEffect, useState } from 'react';
import * as api from './lib/api';
import { ApiError } from './lib/api';
import type { AppConfig, IdsDocumentSummary, TemplateSummary } from './lib/types';
import { HistoryView } from './components/HistoryView';
import { IdsView } from './components/IdsView';
import { OrgPortalView } from './components/OrgPortalView';
import { PlatformAdminView } from './components/PlatformAdminView';
import { SharedReportView } from './components/SharedReportView';
import { TemplatesView } from './components/TemplatesView';
import { ValidateView } from './components/ValidateView';
import { Badge, Callout, Logo, Spinner } from './components/primitives';
import { UpgradeButton } from './components/UpgradeButton';
import { AuthMenu } from './components/AuthMenu';

type View = 'validate' | 'ids' | 'templates' | 'history' | 'org';

const NAV: Array<[View, string]> = [
  ['validate', 'Validate'],
  ['ids', 'IDS'],
  ['templates', 'Rule templates'],
  ['history', 'History'],
];

// Only shown to an account whose org_role is "admin" (see api.getConfig's
// org_role field) -- a plain org member never gets this nav item, same as
// today's every-other-view being unconditionally visible.
const ORG_NAV: [View, string] = ['org', 'Seat pool'];

/** There are four tiers now, so the badge reads the tier rather than guessing. */
const TIER_LABEL: Record<AppConfig['tier'], string> = {
  free: 'Free tier',
  professional: 'Professional',
  team: 'Team',
  enterprise: 'Enterprise',
};

const PAGE_COPY: Record<View, { title: string; blurb: string }> = {
  validate: {
    title: 'Validate an IFC file',
    blurb:
      'Check a model for schema, spatial structure, property, GUID, geometry and classification defects before you submit it — or before you accept it.',
  },
  ids: {
    title: 'Information Delivery Specifications',
    blurb:
      'Read a client’s IDS to see exactly what a compliant model must contain, or build your own rule set and issue it to your suppliers as a standard IDS file.',
  },
  templates: {
    title: 'Rule templates',
    blurb:
      'The built-in baselines, and any rule set you have tailored and saved. Every template is plain JSON you can export and review.',
  },
  history: {
    title: 'History',
    blurb: 'Past validations, with their reports available to re-download.',
  },
  org: {
    title: 'Seat pool',
    blurb: 'Add or remove contractor logins under your Enterprise plan.',
  },
};

/** Set by Stripe's success_url/cancel_url (see backend routes_billing.py), or
 * by a link in a verification/password-reset email (see routes_auth.py).
 * Read once at startup -- there is no client-side router elsewhere in this
 * app, so this stays a plain pathname check rather than pulling one in just
 * for a handful of static landing screens. */
type SpecialLanding =
  | { kind: 'billing'; landing: 'success' | 'cancelled' }
  | { kind: 'seats'; landing: 'success' | 'cancelled' }
  | { kind: 'reset-password'; token: string }
  | { kind: 'verify-email'; token: string }
  | { kind: 'shared-report'; token: string }
  | { kind: 'platform-admin' }
  | null;

function readSpecialLanding(): SpecialLanding {
  const path = window.location.pathname;
  const token = new URLSearchParams(window.location.search).get('token') ?? '';

  if (path === '/billing/success') return { kind: 'billing', landing: 'success' };
  if (path === '/billing/cancelled') return { kind: 'billing', landing: 'cancelled' };
  // Stripe Checkout's redirect after a "Purchase more seats" one-time
  // payment (see routes_org.py's _create_seat_purchase_checkout). The seat
  // limit rises from the webhook, not from landing here -- this is purely
  // "tell the org admin what happened".
  if (path === '/seats/success') return { kind: 'seats', landing: 'success' };
  if (path === '/seats/cancelled') return { kind: 'seats', landing: 'cancelled' };
  if (path === '/reset-password' && token) return { kind: 'reset-password', token };
  if (path === '/verify-email' && token) return { kind: 'verify-email', token };
  // /shared/<token> -- a Share link (see HistoryView), meant to work for a
  // recipient with no ClearIFC account and no reason to ever make one.
  if (path.startsWith('/shared/')) {
    const shareToken = path.slice('/shared/'.length);
    if (shareToken) return { kind: 'shared-report', token: shareToken };
  }
  // ClearIFC's own master portal (CLEARIFC_ADMIN_KEY-gated, not a customer
  // login) -- see PlatformAdminView.tsx and MASTER_GUIDE.md.
  if (path === '/platform-admin') return { kind: 'platform-admin' };
  return null;
}

export default function App() {
  const [view, setView] = useState<View>('validate');
  const [config, setConfig] = useState<AppConfig | null>(null);
  const [specialLanding] = useState<SpecialLanding>(readSpecialLanding);
  const [templates, setTemplates] = useState<TemplateSummary[]>([]);
  const [idsDocuments, setIdsDocuments] = useState<IdsDocumentSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [historyKey, setHistoryKey] = useState(0);

  const loadConfig = useCallback(async () => {
    try {
      setConfig(await api.getConfig());
    } catch (caught) {
      setError(
        caught instanceof ApiError
          ? caught.message
          : 'Could not reach the ClearIFC API. Is the backend running?',
      );
    }
  }, []);

  const loadTemplates = useCallback(async () => {
    try {
      const body = await api.listTemplates();
      setTemplates(body.templates);
    } catch {
      setTemplates([]);
    }
  }, []);

  const loadIdsDocuments = useCallback(async () => {
    try {
      const body = await api.listIdsDocuments();
      setIdsDocuments(body.documents);
    } catch {
      // Expected on the free tier, where saved specifications are gated.
      setIdsDocuments([]);
    }
  }, []);

  useEffect(() => {
    // A shared-report visitor has no account and no reason to make one --
    // skip the config/templates/IDS-document loads this app normally does
    // on startup, none of which that page uses.
    if (specialLanding?.kind === 'shared-report' || specialLanding?.kind === 'platform-admin') return;
    void loadConfig();
    void loadTemplates();
    void loadIdsDocuments();
  }, [loadConfig, loadIdsDocuments, loadTemplates, specialLanding]);

  if (specialLanding?.kind === 'shared-report') {
    return <SharedReportView shareToken={specialLanding.token} />;
  }

  if (specialLanding?.kind === 'platform-admin') {
    return <PlatformAdminView />;
  }

  if (error && !config) {
    return (
      <div className="app">
        <Masthead config={null} view={view} onNavigate={setView} onKeyChange={loadConfig} />
        <main className="main">
          <div className="container">
            <Callout variant="error">
              <strong>The API is unavailable.</strong>
              <div style={{ marginTop: '0.2rem' }}>{error}</div>
              <div style={{ marginTop: '0.4rem' }} className="hint">
                Start it with: <code>uvicorn app.main:app --reload</code> from the backend
                directory.
              </div>
            </Callout>
          </div>
        </main>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="app">
        <Masthead config={null} view={view} onNavigate={setView} onKeyChange={loadConfig} />
        <main className="main">
          <div className="container row">
            <Spinner /> <span className="hint-strong">Starting ClearIFC…</span>
          </div>
        </main>
      </div>
    );
  }

  if (specialLanding) {
    const onContinue = () => {
      window.history.replaceState({}, '', '/');
      window.location.reload();
    };

    if (specialLanding.kind === 'billing') {
      return (
        <BillingLandingScreen landing={specialLanding.landing} config={config} onContinue={onContinue} />
      );
    }
    if (specialLanding.kind === 'seats') {
      return <SeatPurchaseLandingScreen landing={specialLanding.landing} onContinue={onContinue} />;
    }
    if (specialLanding.kind === 'reset-password') {
      return <ResetPasswordScreen token={specialLanding.token} onContinue={onContinue} />;
    }
    return <VerifyEmailScreen token={specialLanding.token} onContinue={onContinue} />;
  }

  const copy = PAGE_COPY[view];

  return (
    <div className="app">
      <Masthead
        config={config}
        view={view}
        onNavigate={setView}
        onKeyChange={async () => {
          await loadConfig();
          await loadTemplates();
          await loadIdsDocuments();
        }}
      />

      <main className="main">
        <div className="container stack">
          <div className="page-head">
            <h1>{copy.title}</h1>
            <p>{copy.blurb}</p>
          </div>

          {view === 'validate' && (
            <ValidateView
              config={config}
              templates={templates}
              idsDocuments={idsDocuments}
              onValidated={() => setHistoryKey((key) => key + 1)}
            />
          )}
          {view === 'ids' && (
            <IdsView
              config={config}
              documents={idsDocuments}
              onDocumentsChanged={loadIdsDocuments}
            />
          )}
          {view === 'templates' && (
            <TemplatesView config={config} templates={templates} onChanged={loadTemplates} />
          )}
          {view === 'history' && <HistoryView key={historyKey} config={config} />}
          {view === 'org' && config.org_role === 'admin' && <OrgPortalView />}
        </div>
      </main>

      <footer className="footer">
        <div className="container footer-inner">
          <span>
            {config.app_name} {config.version} — {config.tagline}
          </span>
          <span>
            Models are processed in temporary storage and deleted immediately after the report is
            generated.
          </span>
        </div>
      </footer>
    </div>
  );
}

/** Landing screen for Stripe's success_url/cancel_url redirect. The webhook
 * (not this page) is what actually upgrades the tier, and it can trail the
 * redirect by a second or two, so this deliberately doesn't claim "you are
 * now Professional" -- it reflects whatever config already says and lets the
 * user re-check. */
function BillingLandingScreen({
  landing,
  config,
  onContinue,
}: {
  landing: 'success' | 'cancelled';
  config: AppConfig | null;
  onContinue: () => void;
}) {
  const upgraded = config?.tier && config.tier !== 'free';

  return (
    <div className="app">
      <main className="main">
        <div className="container" style={{ maxWidth: '480px', margin: '4rem auto' }}>
          <div className="card">
            <div className="card-head">
              <h2>{landing === 'success' ? 'Thanks!' : 'Checkout cancelled'}</h2>
            </div>
            <div className="card-body stack">
              {landing === 'success' ? (
                <p>
                  {upgraded
                    ? 'Your subscription is active and your API key now has Professional access.'
                    : "Payment succeeded and you'll be upgraded automatically within a few seconds. If this still says free tier after a refresh, check that your API key is entered above."}
                </p>
              ) : (
                <p>No charge was made. You can start a subscription again any time.</p>
              )}
              <button type="button" className="btn btn-accent" onClick={onContinue}>
                Continue to ClearIFC
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Landing screen for Stripe's redirect after a "Purchase more seats"
 * one-time payment. Deliberately doesn't claim the seat limit already
 * rose -- that happens from Stripe's webhook, which can trail this
 * redirect by a second or two, same reasoning as BillingLandingScreen. */
function SeatPurchaseLandingScreen({
  landing,
  onContinue,
}: {
  landing: 'success' | 'cancelled';
  onContinue: () => void;
}) {
  return (
    <div className="app">
      <main className="main">
        <div className="container" style={{ maxWidth: '480px', margin: '4rem auto' }}>
          <div className="card">
            <div className="card-head">
              <h2>{landing === 'success' ? 'Payment received' : 'Checkout cancelled'}</h2>
            </div>
            <div className="card-body stack">
              {landing === 'success' ? (
                <p>
                  Your seat pool will update within a few seconds. If it still shows the old
                  count after a refresh of the Seat pool page, check back shortly.
                </p>
              ) : (
                <p>No charge was made. You can request more seats again any time.</p>
              )}
              <button type="button" className="btn btn-accent" onClick={onContinue}>
                Continue to ClearIFC
              </button>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Landing screen for the link in a password-reset email. */
function ResetPasswordScreen({ token, onContinue }: { token: string; onContinue: () => void }) {
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      await api.resetPassword(token, password);
      setDone(true);
    } catch (caught) {
      setError(caught instanceof ApiError ? caught.message : 'Could not reset your password.');
      setBusy(false);
    }
  }

  return (
    <div className="app">
      <main className="main">
        <div className="container" style={{ maxWidth: '420px', margin: '4rem auto' }}>
          <div className="card">
            <div className="card-head">
              <h2>Reset your password</h2>
            </div>
            <div className="card-body stack">
              {done ? (
                <>
                  <p>Your password has been reset. All other sessions have been logged out.</p>
                  <button type="button" className="btn btn-accent" onClick={onContinue}>
                    Log in to ClearIFC
                  </button>
                </>
              ) : (
                <>
                  <input
                    type="password"
                    placeholder="New password (min. 10 characters)"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    autoFocus
                    aria-label="New password"
                  />
                  {error && (
                    <div className="hint" style={{ color: 'var(--fail-600, #c0392b)' }}>
                      {error}
                    </div>
                  )}
                  <button
                    type="button"
                    className="btn btn-accent"
                    disabled={busy || password.length < 10}
                    onClick={submit}
                  >
                    {busy ? 'Resetting…' : 'Reset password'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

/** Landing screen for the link in a verification email -- verifies
 * immediately on load rather than needing a button click, since arriving
 * here at all already proves the link (and therefore the address) is
 * genuine. */
function VerifyEmailScreen({ token, onContinue }: { token: string; onContinue: () => void }) {
  const [state, setState] = useState<'checking' | 'ok' | 'error'>('checking');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void (async () => {
      try {
        await api.verifyEmail(token);
        setState('ok');
      } catch (caught) {
        setError(caught instanceof ApiError ? caught.message : 'Could not verify your email.');
        setState('error');
      }
    })();
  }, [token]);

  return (
    <div className="app">
      <main className="main">
        <div className="container" style={{ maxWidth: '420px', margin: '4rem auto' }}>
          <div className="card">
            <div className="card-head">
              <h2>Verify your email</h2>
            </div>
            <div className="card-body stack">
              {state === 'checking' && (
                <div className="row">
                  <Spinner /> <span className="hint-strong">Verifying…</span>
                </div>
              )}
              {state === 'ok' && <p>Your email address is verified.</p>}
              {state === 'error' && (
                <div className="hint" style={{ color: 'var(--fail-600, #c0392b)' }}>
                  {error}
                </div>
              )}
              {state !== 'checking' && (
                <button type="button" className="btn btn-accent" onClick={onContinue}>
                  Continue to ClearIFC
                </button>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}

function Masthead({
  config,
  view,
  onNavigate,
  onKeyChange,
}: {
  config: AppConfig | null;
  view: View;
  onNavigate: (view: View) => void;
  onKeyChange: () => void | Promise<void>;
}) {
  const [editingKey, setEditingKey] = useState(false);
  const [key, setKey] = useState(api.getApiKey());

  return (
    <header className="masthead">
      <div className="masthead-inner">
        <div className="masthead-top">
          <div className="logo">
            <Logo />
            <div>
              ClearIFC
              <div className="logo-sub">clearifc.com</div>
            </div>
          </div>
          <div className="masthead-spacer" />

          {config && (
            <Badge variant="tier">{TIER_LABEL[config.tier] ?? config.tier}</Badge>
          )}

          {/* A pooled seat under an org, shown to admins and members alike --
              members get read-only context ("you're on someone else's
              plan"), admins get the same note plus the Seat pool nav item
              below. */}
          {config?.org_id && (
            <span className="hint" title="This account is a seat in an org's Enterprise plan">
              {config.org_role === 'admin' ? 'Org admin' : "On org's plan"}
            </span>
          )}

          {config && <UpgradeButton config={config} onAuthChange={onKeyChange} />}

          <AuthMenu onAuthChange={onKeyChange} />

          {editingKey ? (
            <div className="row">
              <input
                type="password"
                value={key}
                placeholder="API key"
                style={{ width: '14rem' }}
                onChange={(event) => setKey(event.target.value)}
                aria-label="API key"
              />
              <button
                type="button"
                className="btn btn-sm btn-accent"
                onClick={async () => {
                  api.setApiKey(key);
                  setEditingKey(false);
                  await onKeyChange();
                }}
              >
                Apply
              </button>
              <button
                type="button"
                className="btn btn-sm"
                onClick={() => {
                  setKey(api.getApiKey());
                  setEditingKey(false);
                }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <button type="button" className="btn btn-sm" onClick={() => setEditingKey(true)}>
              {api.getApiKey() ? 'Change API key' : 'Enter API key'}
            </button>
          )}
        </div>

        <nav className="nav" aria-label="Main">
          {[...NAV, ...(config?.org_role === 'admin' ? [ORG_NAV] : [])].map(([target, label]) => (
            <button
              key={target}
              type="button"
              onClick={() => onNavigate(target)}
              aria-current={view === target ? 'page' : undefined}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>
    </header>
  );
}
